import { lazy, Suspense } from 'react'

import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'

import { useSnapshot } from 'valtio'

import { EditorConflictDialog } from '../cell/cell-types/richtext/EditorConflictModal'
import { ShortcutsModal } from '../shortcuts/ShortcutsModal'
// #region ============== NON-LAZY MODALS

import { AlertDialogModal } from './AlertDialogModal'
import type { ModalResolverProps } from './modal.store'
import { modalStore, resolveModal } from './modal.store'

// #endregion

// #region ============== LAZY LOADED MODALS

const CellHistoryModal = lazy(() => import('../cell/history/CellHistoryModal'))
const MentionsModal = lazy(() => import('../mentions/MentionsModal'))
const VariablesModal = lazy(() => import('../variables/VariablesModal'))

// #endregion

/**
 * Type-safe component that maps modals to their components
 */
function ModalResolver({ payload, onResolve }: ModalResolverProps) {
  return (
    <Suspense fallback={<ModalLoader />}>
      {(() => {
        switch (payload.key) {
          case 'ALERT_DIALOG':
            return (
              <AlertDialogModal
                props={payload.props}
                onResolve={onResolve}
              />
            )

          case 'EDITOR_CONFLICT_DIALOG':
            return (
              <EditorConflictDialog
                props={payload.props}
                onResolve={onResolve}
              />
            )

          case 'SHORTCUTS':
            return (
              <ShortcutsModal
                props={payload.props}
                onResolve={onResolve}
              />
            )

          case 'MENTIONS':
            return (
              <MentionsModal
                props={payload.props}
                onResolve={onResolve}
              />
            )

          case 'VARIABLES':
            return (
              <VariablesModal
                props={payload.props}
                onResolve={onResolve}
              />
            )

          case 'CELL_HISTORY':
            return (
              <CellHistoryModal
                props={payload.props}
                onResolve={onResolve}
              />
            )

          default: {
            const _exhaustive: never = payload
            return _exhaustive
          }
        }
      })()}
    </Suspense>
  )
}

/**
 * Dialog shown while lazy-loaded modals are fetched for the first time.
 */
function ModalLoader() {
  return (
    <Dialog open={true}>
      <DialogContent
        showCloseButton={false}
        className="w-fit"
      >
        <div
          role="status"
          aria-live="polite"
        >
          <Spinner />
          <span className="sr-only">Loading&hellip;</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Headless component that manages and renders modals.
 */
export function ModalManager() {
  const modals = useSnapshot(modalStore).stack

  if (modals.length === 0) return null

  return (
    <>
      {modals.map((modal) => {
        return (
          <ModalResolver
            key={modal.id}
            payload={modal.payload}
            onResolve={(data) => resolveModal(modal.id, data)}
          />
        )
      })}
    </>
  )
}
